import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartLine, 
  faExclamationTriangle, 
  faHistory,
  faGlobe,
  faBell,
  faServer
} from '@fortawesome/free-solid-svg-icons';
import './RemoteMonitoring.scss';

/**
 * RemoteMonitoring component - Displays information about MET Connect®'s remote monitoring solutions
 * 
 * @returns {JSX.Element} The RemoteMonitoring component
 */
const RemoteMonitoring = () => {
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  /**
   * Handle contact button click - navigate to homepage and scroll to contact section
   */
  const handleContactClick = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  // Features of the remote monitoring solution with their descriptions
  const features = [
    {
      icon: faGlobe,
      title: "Web-based Portal Access",
      description: "Access your monitoring data from anywhere through our secure web-based portal interface."
    },
    {
      icon: faChartLine,
      title: "Comprehensive Device Monitoring",
      description: "Monitors all devices on your network and generates specific alerts if user-defined thresholds are reached."
    },
    {
      icon: faBell,
      title: "Multiple Alert Methods",
      description: "Receive alerts by email, push-notification to cell phone or SMS when critical events occur."
    },
    {
      icon: faExclamationTriangle,
      title: "Custom Alarms & Triggers",
      description: "Set your own alarms and triggers for specific events, such as when SCADA Computer applications close."
    },
    {
      icon: faHistory,
      title: "Detailed Event History",
      description: "Access historical data to identify trends and optimize system performance over time."
    },
    {
      icon: faServer,
      title: "Remote Server Control",
      description: "Install our small application daemon to remotely shutdown and restart your hardware when needed."
    }
  ];

  return (
    <div className="remote-monitoring-page">
      <div className="hero-section">
        <div className="hero-overlay"></div>
        <Container className="hero-content">
          <Row className="align-items-center">
            <Col lg={7} className="hero-text">
              <h1 className="hero-title">
                MET Connect<sup>®</sup> Remote Monitoring Solution
              </h1>
            </Col>
            <div className="vertical-divider-container">
              <div className="vertical-divider"></div>
            </div>
            <Col>
              <p className="hero-subtitle">
                Enterprise grade multi-platform network management and monitoring system for your industrial control network.
              </p>
              <div className="hero-features">
                <div className="hero-feature">
                  <FontAwesomeIcon icon={faChartLine} className="feature-icon" />
                  <span>Real-time Network Monitoring</span>
                </div>
                <div className="hero-feature">
                  <FontAwesomeIcon icon={faBell} className="feature-icon" />
                  <span>Custom Alert Systems</span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <section className="solution-overview">
        <Container>
          <Row>
            <Col lg={6} className="mb-4 mb-lg-0">
              <div className="content-block">
                <h2>The MET Connect<sup>®</sup> Monitoring Service</h2>
                <p>
                  Our Enterprise grade multi-platform network management and monitoring system provides comprehensive 
                  event management, alerting, reporting and graphing for all layers of infrastructure – from network 
                  hardware to business application layer.
                </p>
                <p>
                  With our advanced monitoring solution, you can detect potential issues before they impact your operations,
                  ensuring maximum uptime and operational efficiency for your critical systems.
                </p>
              </div>
            </Col>
            <Col lg={6}>
              <div className="image-block">
                <img 
                  src={require('../../images/machine.jpg')} 
                  alt="Industrial Monitoring System" 
                  className="img-fluid rounded shadow"
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="features-section">
        <Container>
          <h2 className="text-center mb-5">Key Features</h2>
          <Row>
            {features.map((feature, index) => (
              <Col lg={6} xl={4} key={index} className="mb-4">
                <Card className="feature-card h-100">
                  <Card.Body>
                    <div className="feature-icon">
                      <FontAwesomeIcon icon={feature.icon} />
                    </div>
                    <Card.Title>{feature.title}</Card.Title>
                    <Card.Text>{feature.description}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="visualization-section">
        <Container>
          <h2 className="text-center mb-5">Advanced Visualization & Control</h2>
          <Row className="mb-5">
            <Col lg={6} className="mb-4 mb-lg-0">
              <div className="content-block">
                <h3>Network Maps</h3>
                <p>
                  Create your own network maps showing at-a-glance node status information.
                  Our intuitive interface allows you to visualize your entire network infrastructure
                  and quickly identify any issues or bottlenecks.
                </p>
              </div>
            </Col>
            <Col lg={6}>
              <div className="image-block">
                <img 
                  src={require('../../images/network-map.png')} 
                  alt="Network Map Visualization" 
                  className="img-fluid rounded shadow"
                />
              </div>
            </Col>
          </Row>

          <Row className="mb-5">
            <Col lg={6} className="order-lg-2 mb-4 mb-lg-0">
              <div className="content-block">
                <h3>Custom Alarms & Event Triggers</h3>
                <p>
                  Set your own alarms and triggers when an event occurs. For example, if your SCADA 
                  Computer's HMI Server application closes, the platform can send an email to the 
                  engineering support team immediately, allowing for quick resolution.
                </p>
              </div>
            </Col>
            <Col lg={6} className="order-lg-1">
              <div className="image-block">
                <img 
                  src={require('../../images/alarm-screen.png')} 
                  alt="Alarm Configuration Screen" 
                  className="img-fluid rounded shadow"
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg={6} className="mb-4 mb-lg-0">
              <div className="content-block">
                <h3>Remote Node Control</h3>
                <p>
                  Have control over your network nodes. For servers, install our small application 
                  daemon and you can remotely shutdown and restart your hardware when needed, 
                  even from remote locations.
                </p>
              </div>
            </Col>
            <Col lg={6}>
              <div className="image-block">
                <img 
                  src={require('../../images/node-control.png')} 
                  alt="Node Control Interface" 
                  className="img-fluid rounded shadow"
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="cta-section">
        <Container className="text-center">
          <h3>Ready to Enhance Your Network Monitoring?</h3>
          <p>
            Contact us today to learn more about our Remote Monitoring Service and how it can help
            keep your critical infrastructure running smoothly and efficiently.
          </p>
          <Button className="btn btn-primary btn-lg" onClick={handleContactClick}>
            Contact Us Today
          </Button>
        </Container>
      </section>
    </div>
  );
};

export default RemoteMonitoring;